/* eslint-disable */
import { ReactNode, useRef, useState } from "react";
import { assembleEmail } from "../core/assembleEmail";
import { isEmailBodyEmpty } from "../core/sanitizer";
import { ParseHtml } from "../core/domAdapter";
import { EmailChrome, AssembledEmail } from "../core/types";
import { RichTextEditor } from "./RichTextEditor";
import { EditorToken } from "./RichTextEditor";
import { resolveTheme } from "./theme";
import { EditorTheme } from "./theme";

/**
 * Generic authoring surface for a branded email, built on RichTextEditor + the
 * core assembleEmail pipeline. Domain-specific concerns (what a "recipient" is,
 * what tokens exist, how templates persist) are all caller-supplied — this
 * component only knows about EmailChrome + a generic recipient shape.
 *
 * Authors never touch HTML: the rich text editor covers formatting, the
 * Preview tab renders the fully assembled email (locked chrome + sanitized
 * body) in a sandboxed iframe per recipient, and Send Test delivers the
 * assembled email to the author's own address before anything goes out for
 * real. Save as Template is opt-in — pass onSaveTemplate to enable it; storage
 * strategy is entirely up to the caller (see useLocalStorageTemplateStore for
 * a ready-made one).
 */

export interface EmailRecipient {
  id: string;
  displayName: string;
  /** Values for {{placeholder}} tokens — resolved per recipient in preview/test/send. */
  values: Record<string, string>;
}

const FIELD_LABEL_STYLE: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#252F4A",
  marginBottom: 6,
};
const DEFAULT_HELPER_TEXT =
  "No HTML needed — format with the toolbar (fonts, colors, lists, tables and more). Details from Insert detail are filled in automatically per recipient. The header, required facts, accept button and footer are always added for you and can't be changed.";

function SaveTemplatePopover({
  onSave,
  onClose,
  theme,
}: {
  onSave: (name: string, description: string) => void;
  onClose: () => void;
  theme: EditorTheme;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const fieldStyle: React.CSSProperties = {
    width: "100%",
    height: 32,
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    padding: "0 10px",
    fontSize: 12,
    marginBottom: 10,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
  };

  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 20 }}
        onClick={onClose}
      />
      <div
        style={{
          position: "absolute",
          bottom: 34,
          left: 0,
          zIndex: 30,
          background: "#fff",
          width: 260,
          border: `1px solid ${theme.borderSubtle}`,
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          padding: 14,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#252F4A",
            marginBottom: 10,
          }}
        >
          Save as Template
        </div>
        <label
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#4B5675",
            display: "block",
            marginBottom: 4,
          }}
        >
          Name *
        </label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Quarterly follow-up"
          style={fieldStyle}
        />
        <label
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#4B5675",
            display: "block",
            marginBottom: 4,
          }}
        >
          Description{" "}
          <span style={{ fontWeight: 400, color: "#99A1B7" }}>(Optional)</span>
        </label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="When to use this"
          style={fieldStyle}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} style={ghostBtnStyle(theme)}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              name.trim() && onSave(name.trim(), description.trim())
            }
            disabled={!name.trim()}
            style={primaryBtnStyle(theme, !name.trim())}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
}

function primaryBtnStyle(
  theme: EditorTheme,
  disabled?: boolean
): React.CSSProperties {
  return {
    height: 28,
    padding: "0 12px",
    borderRadius: 6,
    border: "none",
    fontSize: 12,
    fontWeight: 600,
    background: disabled ? "#DBDFE9" : theme.accent,
    color: "#fff",
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "Inter, sans-serif",
  };
}
function secondaryBtnStyle(
  theme: EditorTheme,
  disabled?: boolean
): React.CSSProperties {
  return {
    height: 32,
    padding: "0 14px",
    borderRadius: 6,
    border: `1px solid ${theme.accent}`,
    fontSize: 12,
    fontWeight: 600,
    background: "transparent",
    color: disabled ? "#DBDFE9" : theme.accent,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "Inter, sans-serif",
    opacity: disabled ? 0.6 : 1,
  };
}
function ghostBtnStyle(
  theme: EditorTheme,
  disabled?: boolean
): React.CSSProperties {
  return {
    height: 28,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${theme.border}`,
    fontSize: 12,
    fontWeight: 600,
    background: "#F5F8FA",
    color: "#252F4A",
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "Inter, sans-serif",
    opacity: disabled ? 0.6 : 1,
  };
}

/** Mirrors RichTextEditor's "Insert detail" menu, but inserts at the caret of a plain text input (Subject) rather than into contentEditable. */
function SubjectTokenMenu({
  tokens,
  onInsert,
  theme,
}: {
  tokens: EditorToken[];
  onInsert: (token: string) => void;
  theme: EditorTheme;
}) {
  const [open, setOpen] = useState(false);
  if (tokens.length === 0) return null;
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          height: 22,
          padding: "0 8px",
          borderRadius: 5,
          cursor: "pointer",
          fontSize: 11,
          fontWeight: 600,
          fontFamily: "Inter, sans-serif",
          border: open
            ? `1px solid ${theme.accent}`
            : `1px solid ${theme.border}`,
          background: open ? theme.accentTint : "#fff",
          color: open ? theme.accent : theme.text,
        }}
      >
        {"{ } Insert detail ▾"}
      </button>
      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 20 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: 26,
              right: 0,
              zIndex: 30,
              background: "#fff",
              minWidth: 170,
              border: `1px solid ${theme.borderSubtle}`,
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              overflow: "hidden",
              padding: 6,
            }}
          >
            {tokens.map((t) => (
              <div
                key={t.token}
                onClick={() => {
                  onInsert(t.token);
                  setOpen(false);
                }}
                style={{
                  padding: "6px 10px",
                  cursor: "pointer",
                  borderRadius: 5,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#F0F2FF")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div
                  style={{ fontSize: 12, fontWeight: 600, color: "#252F4A" }}
                >
                  {t.label}
                </div>
                <div style={{ fontSize: 11, color: theme.textMuted }}>
                  {t.token}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export interface EmailComposerProps {
  subject: string;
  /** Editor HTML, may contain {{placeholders}}. */
  body: string;
  onSubjectChange: (s: string) => void;
  onBodyChange: (b: string) => void;
  chrome: EmailChrome;
  recipients: EmailRecipient[];
  senderName: string;
  /** Where "Send Test" delivers to — shown in the confirmation copy. */
  testEmail: string;
  /** Personalisation tokens shown in the editor's "Insert detail" menu. */
  tokens?: EditorToken[];
  /** Enables "Save as Template" when provided; storage is entirely up to the caller. */
  onSaveTemplate?: (input: {
    name: string;
    description: string;
    subject: string;
    body: string;
  }) => void;
  /** Called (in addition to the built-in confirmation) when Send Test fires — hook in a real test-send API here. */
  onSendTest?: (rendered: AssembledEmail, testEmail: string) => void;
  /** Allows consumers to defer test delivery until their draft is persisted. */
  sendTestEnabled?: boolean;
  theme?: Partial<EditorTheme>;
  helperText?: string;
  parseHtml?: ParseHtml;
  /** 'editor' (default) canonicalises the body at assembly; 'imported' preserves a file-imported body as-is (security pass only). */
  bodyMode?: "editor" | "imported";
  /** Wrap the body in the standard header/facts/CTA/footer. Defaults per mode: true for editor bodies, false for imported ones. */
  includeChrome?: boolean;
  /** Optional controls rendered above the editable subject and body. */
  editorHeader?: ReactNode;
}

export function EmailComposer({
  subject,
  body,
  onSubjectChange,
  onBodyChange,
  chrome,
  recipients,
  senderName,
  testEmail,
  tokens = [],
  onSaveTemplate,
  onSendTest,
  sendTestEnabled = true,
  theme: themeOverride,
  helperText = DEFAULT_HELPER_TEXT,
  parseHtml,
  bodyMode,
  includeChrome,
  editorHeader,
}: EmailComposerProps) {
  const theme = resolveTheme(themeOverride);
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const [previewRecipientId, setPreviewRecipientId] = useState(
    recipients[0]?.id ?? ""
  );
  const [testSentTo, setTestSentTo] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [savedAs, setSavedAs] = useState("");
  const subjectRef = useRef<HTMLInputElement>(null);

  const previewRecipient =
    recipients.find((r) => r.id === previewRecipientId) ?? recipients[0];
  const bodyEmpty = isEmailBodyEmpty(body, { parseHtml });

  const rendered = previewRecipient
    ? assembleEmail({
        chrome,
        subject,
        bodyHtml: body,
        bodyMode,
        includeChrome,
        values: { ...previewRecipient.values, sender_name: senderName },
        parseHtml,
      })
    : null;

  function handleSendTest() {
    if (!rendered || !sendTestEnabled) return;
    onSendTest?.(rendered, testEmail);
    setTestSentTo(testEmail);
    setTimeout(() => setTestSentTo(""), 4000);
  }

  function handleSaveTemplate(name: string, description: string) {
    onSaveTemplate?.({ name, description, subject, body });
    setSaveOpen(false);
    setSavedAs(name);
    setTimeout(() => setSavedAs(""), 4000);
  }

  function insertSubjectToken(token: string) {
    const el = subjectRef.current;
    const start = el?.selectionStart ?? subject.length;
    const end = el?.selectionEnd ?? subject.length;
    onSubjectChange(subject.slice(0, start) + token + subject.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + token.length;
      el?.setSelectionRange(pos, pos);
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        style={{
          display: "flex",
          gap: 20,
          borderBottom: `1px solid ${theme.borderSubtle}`,
        }}
      >
        {(["edit", "preview"] as const).map((t) => (
          <div
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "6px 2px 8px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              color: tab === t ? theme.accent : theme.textMuted,
              borderBottom:
                tab === t
                  ? `2px solid ${theme.accent}`
                  : "2px solid transparent",
              marginBottom: -1,
            }}
          >
            {t === "edit" ? "Edit" : "Preview"}
          </div>
        ))}
      </div>

      {tab === "edit" ? (
        <>
          {editorHeader}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <label style={{ ...FIELD_LABEL_STYLE, marginBottom: 0 }}>
                Subject *
              </label>
              <SubjectTokenMenu
                tokens={tokens}
                onInsert={insertSubjectToken}
                theme={theme}
              />
            </div>
            <input
              ref={subjectRef}
              value={subject}
              onChange={(e) => onSubjectChange(e.target.value)}
              style={{
                width: "100%",
                height: 36,
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                padding: "0 12px",
                fontSize: 13,
                outline: "none",
                background: "#fff",
                color: "#4B5675",
                boxSizing: "border-box",
                fontFamily: "Inter, sans-serif",
              }}
            />
          </div>

          <div>
            <label style={FIELD_LABEL_STYLE}>Email Body *</label>
            <RichTextEditor
              html={body}
              onChange={onBodyChange}
              tokens={tokens}
              theme={themeOverride}
              parseHtml={parseHtml}
              supportsImages={bodyMode === "imported"}
            />
            <div
              style={{
                fontSize: 11,
                color: theme.textMuted,
                marginTop: 4,
                lineHeight: "16px",
              }}
            >
              {helperText}
            </div>
          </div>
        </>
      ) : (
        <>
          {recipients.length > 1 && (
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: theme.textMuted,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                Preview as
              </span>
              {recipients.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setPreviewRecipientId(r.id)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "Inter, sans-serif",
                    border:
                      r.id === previewRecipient?.id
                        ? `1px solid ${theme.accent}`
                        : `1px solid ${theme.border}`,
                    background:
                      r.id === previewRecipient?.id ? theme.accentTint : "#fff",
                    color:
                      r.id === previewRecipient?.id ? theme.accent : "#4B5675",
                  }}
                >
                  {r.displayName}
                </button>
              ))}
            </div>
          )}

          {rendered && previewRecipient && (
            <>
              <div
                style={{
                  border: `1px solid ${theme.borderSubtle}`,
                  borderRadius: 8,
                  padding: "10px 14px",
                  background: "#fff",
                }}
              >
                <div style={{ fontSize: 11, color: theme.textMuted }}>
                  To: {previewRecipient.displayName} &nbsp;·&nbsp; From:{" "}
                  {senderName} via {chrome.brand.name}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#071437",
                    marginTop: 4,
                  }}
                >
                  {rendered.subject}
                </div>
              </div>
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={rendered.html}
                style={{
                  width: "100%",
                  height: 460,
                  border: `1px solid ${theme.borderSubtle}`,
                  borderRadius: 8,
                  background: "#F3F4F6",
                }}
              />
            </>
          )}
        </>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={handleSendTest}
          disabled={!sendTestEnabled || !subject.trim() || bodyEmpty}
          style={secondaryBtnStyle(
            theme,
            !sendTestEnabled || !subject.trim() || bodyEmpty
          )}
        >
          Send Test Email
        </button>
        {onSaveTemplate && (
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setSaveOpen((o) => !o)}
              disabled={!subject.trim() || bodyEmpty}
              style={ghostBtnStyle(theme, !subject.trim() || bodyEmpty)}
            >
              Save as Template
            </button>
            {saveOpen && (
              <SaveTemplatePopover
                onSave={handleSaveTemplate}
                onClose={() => setSaveOpen(false)}
                theme={theme}
              />
            )}
          </div>
        )}
        {testSentTo ? (
          <span style={{ fontSize: 12, color: "#039855", fontWeight: 500 }}>
            ✓ Test sent to {testSentTo}
          </span>
        ) : savedAs ? (
          <span style={{ fontSize: 12, color: "#039855", fontWeight: 500 }}>
            ✓ Saved as "{savedAs}" — pick it from the template list next time
          </span>
        ) : (
          <span style={{ fontSize: 11, color: theme.textMuted }}>
            Delivers this email to you ({testEmail}) — not to recipients.
          </span>
        )}
      </div>
    </div>
  );
}
